// The first thing on Today when leads are waiting for someone: how many are unattended for over a day
// (mine, and my team's for a manager) and how many people came back. Each line opens the Leads list
// already filtered to exactly those leads.
import { Link } from '@tanstack/react-router';
import { BellRing, ChevronRight } from 'lucide-react';
import { useAuth } from '../../lib/auth.js';
import { useAttentionSummary } from '../leads/api.js';

const plural = (n: number) => (n === 1 ? 'lead' : 'leads');

export function AttentionCard() {
  const { can } = useAuth();
  const summary = useAttentionSummary(can('leads.lead.read'));
  const s = summary.data;
  if (!s || (s.unattendedMine === 0 && s.unattendedTeam === 0 && s.reEnquiredMine === 0)) return null;

  const lines = [
    {
      key: 'unattended',
      n: s.unattendedMine,
      text: `${s.unattendedMine} ${plural(s.unattendedMine)} unattended for over ${s.afterHours} h`,
      search: { attention: 'unattended' },
      urgent: true,
    },
    {
      key: 'team',
      n: s.unattendedTeam,
      text: `${s.unattendedTeam} ${plural(s.unattendedTeam)} in your team unattended for over ${s.afterHours} h`,
      search: { attention: 'unattended', team: 1 },
      urgent: true,
    },
    {
      key: 'reenquired',
      n: s.reEnquiredMine,
      text: `${s.reEnquiredMine} re-enquired`,
      search: { reEnquired: 1 },
      urgent: false,
    },
  ].filter((l) => l.n > 0);

  return (
    <section
      aria-label="Leads that need attention"
      data-testid="attention-card"
      className="rounded-2xl border border-warning/40 bg-warning/10 p-4 shadow-soft sm:p-5"
    >
      <h2 className="mb-2 flex items-center gap-2 font-display text-lg font-semibold text-text">
        <BellRing className="h-5 w-5 text-warning" aria-hidden="true" />
        Leads waiting for you
      </h2>
      <ul className="space-y-1.5">
        {lines.map((l) => (
          <li key={l.key}>
            <Link
              to="/leads"
              search={l.search as never}
              data-testid={`attention-${l.key}`}
              className="flex min-h-11 items-center justify-between gap-3 rounded-xl bg-surface px-4 py-2 text-sm font-semibold text-text transition-colors hover:bg-surface-hover"
            >
              <span className={l.urgent ? 'text-warning' : 'text-text'}>{l.text}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-text-faint" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
